/**
 * useAiAgent.ts – React hook for AI agent chat with tool-calling
 *
 * This is the decoupled, framework-agnostic version of the Zaunplaner's AiHook.ts.
 * All application-specific coupling points (APP global, raw-loader imports,
 * oc-commons) have been replaced by a configuration object.
 *
 * @example
 * ```tsx
 * const chat = useAiAgent({
 *   provider: "google",
 *   model: "google/gemini-3-flash-preview",
 *   proxyUrl: "/aiproxy",
 *   systemPromptParts: [instructionsMd, evaluateMd, faqMd],
 *   evaluateToolDescription: evaluateDescription,
 *   evaluate: myEvaluateFunction,
 *   getAppContext: () => JSON.stringify(getCurrentUIState()),
 * });
 * ```
 */
import { useState, useCallback, useRef, useEffect } from "react";
import { generateText, tool, stepCountIs, jsonSchema } from "ai";
import { createAiModel, NANO_BANANA_2_MODEL } from "./AiProviderFactory";
import { consumePendingScreenshots } from "./captureScreen";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AiAgentMessage {
  id: string | null;
  role: "system" | "user" | "assistant" | "data" | "option" | "error" | "log" | "code";
  content: {
    type: "text" | "option" | "image";
    text: { annotations: any[]; value: string };
    imageData?: string;
    callback?: () => void;
  }[];
  messageIndex: number;
  created_at: any;
}

export interface AiAgentOptions {
  /** AI provider name (default: "google") */
  provider?: string;
  /** Model name, e.g. "google/gemini-3-flash-preview" */
  model: string;
  /** Vision model name – used when images are present in the chat (optional) */
  visionModel?: string;
  /** Provider for image generation. Nano Banana 2 currently requires "google". */
  imageGenerationProvider?: string;
  /** Image model name for fence render generation (default: Nano Banana 2 / Gemini 3.1 Flash Image). */
  imageGenerationModel?: string;
  /**
   * Gemini GenerateContent API version for image generation.
   * Gemini 3.1 Flash Image GA is exposed through the v1 endpoint.
   * Defaults to "v1".
   */
  imageGenerationApiVersion?: "v1" | "v1beta";
  /** Base URL of the AI proxy endpoint (default: "/aiproxy") */
  proxyUrl?: string;
  /** Application title sent in proxy headers */
  appTitle?: string;

  /**
   * Parts of the system prompt, joined with "\n".
   * Typically: [instructionsMd, evaluateDescription, faqMd, glossarMd]
   */
  systemPromptParts: string[];

  /**
   * Full description of the evaluate tool (shown to the AI as tool description).
   * Should include the TypeScript API interface and usage examples.
   */
  evaluateToolDescription: string;

  /**
   * The evaluate function to call when the AI uses the "evaluate" tool.
   * Create this with `createEvaluate()` from evaluate.ts.
   */
  evaluate: (args: { jsCode: string }) => Promise<string>;

  /**
   * Optional: Returns app context to append to each user message.
   * The returned string is wrapped in <app-context>...</app-context> tags.
   */
  getAppContext?: () => string;

  /** Max tool-calling steps per message (default: 20) */
  maxSteps?: number;

  /** CSRF token for proxy requests (auto-detected from meta tag if not provided) */
  csrfToken?: string;

  /** When true, every tool result is shown as a log message in the chat – not just errors. (default: true) */
  verboseToolResults?: boolean;

  // ── Fallback messages ──────────────────────────────────────────────────────

  /** Shown when the AI ran tool calls but returned no text (default: "Done. What would you like me to do next?") */
  msgToolCallsDone?: string;
  /** Shown when the AI returns an empty response without tool calls (default: "Sorry, I don't have an answer for that.") */
  msgEmptyResponse?: string;
  /** Shown when the user cancels a running request (default: "Cancelled.") */
  msgCancelled?: string;
  /** Prefix for connection error messages (default: "Connection error: ") */
  msgConnectionErrorPrefix?: string;
}

export type AiFenceRenderAspectRatio =
  | "1:1"
  | "2:3"
  | "3:2"
  | "3:4"
  | "4:3"
  | "4:5"
  | "5:4"
  | "9:16"
  | "16:9"
  | "21:9";

export type AiFenceRenderImageSize = "1K" | "2K" | "4K";
export interface AiFenceRenderRequest {
  /** Normal 3D scene snapshot as PNG/JPEG data URL. */
  snapshotDataUrl: string;
  /** White = physical fence product must be preserved, black = may be changed. */
  preserveMaskDataUrl: string;
  /** Depth map as PNG/JPEG data URL. */
  depthMapDataUrl: string;
  /** Optional previously generated render image to edit iteratively. */
  editBaseImageDataUrl?: string;
  /** Optional user additions, e.g. garden style, weather, background. */
  prompt?: string;
  /** Output aspect ratio. Defaults to 16:9. */
  aspectRatio?: AiFenceRenderAspectRatio;
  /** Gemini image resolution. Defaults to 2K. */
  imageSize?: AiFenceRenderImageSize;
  /** Optional deterministic seed when supported by the provider. */
  seed?: number;
  /** Optional per-call override for the configured image model. */
  modelName?: string;
}

export interface AiFenceRenderResult {
  imageDataUrl: string;
  mimeType: string;
  base64: string;
  /** Approximate billed request cost in USD if returned by the provider/proxy. */
  costUsd?: number;
  warnings?: string[];
  providerMetadata?: unknown;
}

// ── Module-level singleton store ──────────────────────────────────────────────
// Survives component re-mounts (SPA page switches) but is lost on full reload.

let _persistedMessages: AiAgentMessage[] = [];
let _persistedError: string | null = null;
let _persistedIsLoading = false;

let _registeredSetMessages: React.Dispatch<React.SetStateAction<AiAgentMessage[]>> | null = null;
let _registeredSetIsLoading: React.Dispatch<React.SetStateAction<boolean>> | null = null;
let _registeredSetError: React.Dispatch<React.SetStateAction<string | null>> | null = null;

// ── Helpers ───────────────────────────────────────────────────────────────────

interface ParsedImageData {
  data: string;
  mediaType?: string;
}

function parseImageDataUrl(dataUrl: string): ParsedImageData {
  const match = dataUrl.match(/^data:(image\/[^;]+);base64,([\s\S]+)$/);
  if (!match) {
    return { data: dataUrl };
  }

  return {
    mediaType: match[1],
    data: match[2],
  };
}

interface GeminiInlineData {
  data?: string;
  mimeType?: string;
  mime_type?: string;
}

interface GeminiGenerateContentResponse {
  candidates?: Array<{
    finishReason?: string;
    finish_reason?: string;
    content?: {
      parts?: Array<{
        text?: string;
        inlineData?: GeminiInlineData;
        inline_data?: GeminiInlineData;
      }>;
    };
  }>;
  promptFeedback?: {
    blockReason?: string;
    block_reason?: string;
  };
  error?: {
    code?: number | string;
    message?: string;
    status?: string;
  };
  usageMetadata?: unknown;
  [key: string]: unknown;
}

function createGeminiInlineDataPart(dataUrl: string) {
  const parsed = parseImageDataUrl(dataUrl);

  if (!parsed.mediaType) {
    throw new Error("Reference image must be a base64 image data URL.");
  }

  return {
    inlineData: {
      mimeType: parsed.mediaType,
      data: parsed.data,
    },
  };
}

function createOpenRouterImageUrlPart(dataUrl: string) {
  return {
    type: "image_url" as const,
    image_url: {
      url: dataUrl,
    },
  };
}

function createOpenRouterTextPart(text: string) {
  return {
    type: "text" as const,
    text,
  };
}

function buildGeminiFenceRenderParts(args: {
  prompt: string;
  snapshotDataUrl: string;
  preserveMaskDataUrl: string;
  depthMapDataUrl: string;
  editBaseImageDataUrl?: string;
  hasEditBaseImage: boolean;
}) {
  const parts: any[] = [
    {
      text: args.prompt,
    },
    {
      text:
        "PRIMARY GEOMETRY SOURCE (HIGHEST PRIORITY): Reference image A. " +
        "Use only this snapshot for fence layout, dimensions, element positions and proportions.",
    },
    createGeminiInlineDataPart(args.snapshotDataUrl),
    {
      text:
        "MASK FOR REFERENCE IMAGE A ONLY: Reference image B. " +
        "Apply this preserve mask exclusively to the snapshot geometry from image A.",
    },
    createGeminiInlineDataPart(args.preserveMaskDataUrl),
    {
      text:
        "DEPTH FOR REFERENCE IMAGE A ONLY: Reference image C. " +
        "Use this depth map exclusively for perspective/depth consistency of image A.",
    },
    createGeminiInlineDataPart(args.depthMapDataUrl),
  ];

  if (args.editBaseImageDataUrl) {
    parts.push(
      {
        text:
          "EDIT BASE (SECONDARY PRIORITY): Reference image D. " +
          "Use as visual baseline for unchanged areas, but never override geometry constraints from image A/B/C.",
      },
      createGeminiInlineDataPart(args.editBaseImageDataUrl)
    );
  }

  return parts;
}

function buildOpenRouterFenceRenderContent(args: {
  prompt: string;
  snapshotDataUrl: string;
  preserveMaskDataUrl: string;
  depthMapDataUrl: string;
  editBaseImageDataUrl?: string;
}) {
  const content: any[] = [
    createOpenRouterTextPart(args.prompt),
    createOpenRouterTextPart(
      "PRIMARY GEOMETRY SOURCE (HIGHEST PRIORITY): Reference image A. " +
        "Use only this snapshot for fence layout, dimensions, element positions and proportions."
    ),
    createOpenRouterImageUrlPart(args.snapshotDataUrl),
    createOpenRouterTextPart(
      "MASK FOR REFERENCE IMAGE A ONLY: Reference image B. " +
        "Apply this preserve mask exclusively to the snapshot geometry from image A."
    ),
    createOpenRouterImageUrlPart(args.preserveMaskDataUrl),
    createOpenRouterTextPart(
      "DEPTH FOR REFERENCE IMAGE A ONLY: Reference image C. " +
        "Use this depth map exclusively for perspective/depth consistency of image A."
    ),
    createOpenRouterImageUrlPart(args.depthMapDataUrl),
  ];

  if (args.editBaseImageDataUrl) {
    content.push(
      createOpenRouterTextPart(
        "EDIT BASE (SECONDARY PRIORITY): Reference image D. " +
          "Use as visual baseline for unchanged areas, but never override geometry constraints from image A/B/C."
      ),
      createOpenRouterImageUrlPart(args.editBaseImageDataUrl)
    );
  }

  return content;
}

function getImageDataUrlParts(dataUrl: string): { mimeType: string; base64: string } | null {
  const match = dataUrl.match(/^data:(image\/[^;]+);base64,([\s\S]+)$/i);
  if (!match) return null;

  return {
    mimeType: match[1],
    base64: match[2],
  };
}

function getOpenRouterGeneratedImage(response: any): { base64: string; mimeType: string } | null {
  const choices = Array.isArray(response?.choices) ? response.choices : [];
  for (const choice of choices) {
    const message = choice?.message;

    const images = Array.isArray(message?.images) ? message.images : [];
    for (const image of images) {
      const imageUrl = image?.image_url?.url;
      if (typeof imageUrl === "string") {
        const dataParts = getImageDataUrlParts(imageUrl);
        if (dataParts) {
          return {
            base64: dataParts.base64,
            mimeType: dataParts.mimeType,
          };
        }
      }
    }

    const contentParts = Array.isArray(message?.content) ? message.content : [];
    for (const part of contentParts) {
      const imageUrl = part?.image_url?.url;
      if (typeof imageUrl === "string") {
        const dataParts = getImageDataUrlParts(imageUrl);
        if (dataParts) {
          return {
            base64: dataParts.base64,
            mimeType: dataParts.mimeType,
          };
        }
      }
    }
  }

  return null;
}

function getGeminiGeneratedImage(response: GeminiGenerateContentResponse): {
  base64: string;
  mimeType: string;
} | null {
  for (const candidate of response.candidates ?? []) {
    for (const part of candidate.content?.parts ?? []) {
      const inlineData = part.inlineData ?? part.inline_data;
      if (inlineData?.data) {
        return {
          base64: inlineData.data,
          mimeType: inlineData.mimeType ?? inlineData.mime_type ?? "image/png",
        };
      }
    }
  }

  return null;
}

function getGeminiResponseDetails(response: GeminiGenerateContentResponse): string {
  const textParts = (response.candidates ?? [])
    .flatMap((candidate) => candidate.content?.parts ?? [])
    .map((part) => part.text?.trim())
    .filter((text): text is string => Boolean(text));

  const finishReasons = (response.candidates ?? [])
    .map((candidate) => candidate.finishReason ?? candidate.finish_reason)
    .filter((reason): reason is string => Boolean(reason));

  const blockReason = response.promptFeedback?.blockReason ?? response.promptFeedback?.block_reason;

  return [
    blockReason ? `blockReason=${blockReason}` : "",
    finishReasons.length > 0 ? `finishReason=${finishReasons.join(",")}` : "",
    textParts.length > 0 ? `text=${textParts.join(" ")}` : "",
  ]
    .filter(Boolean)
    .join("; ");
}

function parseOptionalNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function getResponseCostUsd(response: any): number | null {
  const directCost = parseOptionalNumber(response?.usage?.cost);
  if (directCost !== null) return directCost;

  const metadataCost = parseOptionalNumber(response?.usageMetadata?.cost);
  if (metadataCost !== null) return metadataCost;

  const promptCost = parseOptionalNumber(
    response?.usage?.cost_details?.upstream_inference_prompt_cost
  );
  const completionCost = parseOptionalNumber(
    response?.usage?.cost_details?.upstream_inference_completions_cost
  );
  if (promptCost !== null && completionCost !== null) {
    return promptCost + completionCost;
  }

  return null;
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
}

function detectImageMimeTypeFromBytes(bytes: Uint8Array): string | null {
  if (bytes.length >= 8) {
    const isPng =
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47 &&
      bytes[4] === 0x0d &&
      bytes[5] === 0x0a &&
      bytes[6] === 0x1a &&
      bytes[7] === 0x0a;
    if (isPng) return "image/png";
  }

  if (bytes.length >= 3) {
    const isJpeg = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
    if (isJpeg) return "image/jpeg";
  }

  if (bytes.length >= 12) {
    const isWebp =
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50;
    if (isWebp) return "image/webp";
  }

  return null;
}

function buildFenceRenderPrompt(userPrompt?: string, hasEditBaseImage?: boolean): string {
  const userInstructions = userPrompt?.trim()
    ? userPrompt.trim()
    : "Realistic modern private garden, natural daylight, professional architectural product render.";

  const editSection = hasEditBaseImage
    ? `

    Additional edit reference:
    4. Previously generated render image. Use this as the visual baseline and keep composition and mood unless user wishes explicitly request changes.

    Editing behavior:
    - Start from the previous render and apply only the requested changes.
    - Keep all unchanged areas visually consistent.
    - Do not introduce unrelated changes.`
    : ``;

  return `Create a photorealistic architectural product render from the provided reference images.

    Priority rules (must be followed in this order):
    1. Snapshot geometry source + its mask/depth (highest priority).
    2. Optional previous render for visual continuity.
    If any references conflict, always follow the higher-priority source.

    Reference image order:
    1. The exact Three.js snapshot of the configured fence system.
    2. A preserve mask for the snapshot only: white pixels mark the physical product that must remain geometrically faithful; black pixels may be changed freely.
    3. A depth map for the snapshot only: use it to preserve perspective, spatial layout and object relationships of the snapshot.
    ${editSection}

    Strict product constraints:
    - The fence system is a real configurable physical product. Do not create unrealistic expectations.
    - Keep all fence posts, fence panels, gates, fillings, visible frames and product-relevant hardware in the same position, size, proportions, number and spacing as in the original snapshot.
    - Do not add, remove, resize, rotate or move product parts.
    - Preserve mask and depth map apply only to the 3D snapshot reference.
    - Do not change the profile direction, filling type, panel rhythm, gate geometry, opening direction or visible construction logic.
    - Preserve the silhouette and edges of the white-mask product areas as closely as possible.
    - The generated result may improve material realism, lighting and shadows, but the physical product geometry must remain faithful.

    Allowed creative changes:
    - Improve sky, weather, lighting, shadows, background, ground, garden, terrace, paving, plants and surrounding architecture.
    - You may add a realistic house, lawn, trees, flower beds or other plausible outdoor context outside the protected product area.
    - Make the image look like a realistic marketing-style render while keeping the configured fence trustworthy.

    User wishes:
    ${userInstructions}

    Return only the final image.`;
}

export function createMessage(
  role: AiAgentMessage["role"],
  messages: string[],
  messageIndex?: number,
  callback?: () => void,
  image?: string
): AiAgentMessage {
  return {
    id: Date.now().toString(),
    role,
    content: [
      ...(image
        ? [
            {
              type: "image" as const,
              text: { annotations: [] as any[], value: "" },
              imageData: image,
            },
          ]
        : []),
      ...messages.map((value) => ({
        type: (role === "option" ? "option" : "text") as "text" | "option" | "image",
        text: { annotations: [] as any[], value },
        callback,
      })),
    ],
    messageIndex: messageIndex || Math.floor(Math.random() * 1000),
    created_at: null,
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAiAgent(options: AiAgentOptions) {
  const {
    provider = "google",
    model: modelName,
    visionModel,
    imageGenerationProvider = "google",
    imageGenerationModel = NANO_BANANA_2_MODEL,
    imageGenerationApiVersion = "v1",
    proxyUrl = "/aiproxy",
    appTitle = "AI Agent",
    systemPromptParts,
    evaluateToolDescription,
    evaluate,
    getAppContext,
    maxSteps = 20,
    csrfToken,
    verboseToolResults = true,
    msgToolCallsDone = "Done. What would you like me to do next?",
    msgEmptyResponse = "Sorry, I don't have an answer for that.",
    msgCancelled = "Cancelled.",
    msgConnectionErrorPrefix = "Connection error: ",
  } = options;

  const [messages, setMessagesState] = useState<AiAgentMessage[]>(_persistedMessages);
  const [isLoading, setIsLoadingState] = useState(_persistedIsLoading);
  const [error, setError] = useState<string | null>(_persistedError);
  const [activeModelName, setActiveModelName] = useState<string>(modelName);
  const [aiRenderIsLoading, setAiRenderIsLoading] = useState(false);
  const [aiRenderError, setAiRenderError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    _registeredSetMessages = setMessagesState;
    _registeredSetIsLoading = setIsLoadingState;
    _registeredSetError = setError;
    return () => {
      mountedRef.current = false;
      if (_registeredSetMessages === setMessagesState) _registeredSetMessages = null;
      if (_registeredSetIsLoading === setIsLoadingState) _registeredSetIsLoading = null;
      if (_registeredSetError === setError) _registeredSetError = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setMessages: typeof setMessagesState = useCallback((action) => {
    const computeNext = (prev: AiAgentMessage[]) => {
      const next = typeof action === "function" ? action(prev) : action;
      _persistedMessages = next;
      return next;
    };
    const setter = _registeredSetMessages;
    if (setter) {
      setter(computeNext);
    } else {
      computeNext(_persistedMessages);
    }
  }, []);

  const setErrorPersisted = useCallback((err: string | null) => {
    _persistedError = err;
    _registeredSetError?.(err);
  }, []);

  const safeSetIsLoading = useCallback((val: boolean) => {
    _persistedIsLoading = val;
    _registeredSetIsLoading?.(val);
  }, []);

  const createProxyFetch = useCallback((): typeof globalThis.fetch => {
    const effectiveCsrfToken =
      csrfToken ??
      (typeof document !== "undefined"
        ? document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
        : null) ??
      "";

    return async (url: RequestInfo | URL, fetchOptions?: RequestInit) => {
      const urlStr = typeof url === "string" ? url : url.toString();
      const urlObj = new URL(urlStr, window.location.origin);
      const pathIndex = urlObj.pathname.indexOf(proxyUrl);
      if (pathIndex >= 0) {
        const targetPath = urlObj.pathname.substring(pathIndex + proxyUrl.length);
        const newUrl = urlObj.origin + proxyUrl + urlObj.search;
        fetchOptions = fetchOptions || {};
        // Sanitise header values: HTTP headers only allow ISO-8859-1 characters.
        // Non-latin characters in appTitle (e.g. em-dash, Umlauts) would cause
        // "Failed to read the 'headers' property from 'RequestInit'" in fetch().
        const safeTitle = appTitle.replace(/[^\x20-\x7E]/g, "_");
        fetchOptions.headers = {
          ...(fetchOptions.headers || {}),
          "X-Target-Path": targetPath,
          "X-CSRF-TOKEN": effectiveCsrfToken,
          "HTTP-Referer": window.location.origin,
          "X-Title": safeTitle,
        };
        return fetch(newUrl, fetchOptions);
      }
      return fetch(url, fetchOptions);
    };
  }, [appTitle, csrfToken, proxyUrl]);

  const postMessage = useCallback(
    async (userContent: string, image?: string) => {
      safeSetIsLoading(true);
      setErrorPersisted(null);

      const userMsg = createMessage("user", [userContent], undefined, undefined, image);
      setMessages((prev) => [...prev, userMsg]);

      try {
        // Build history from existing messages
        const history = messages
          .map((m) => {
            if (m.role !== "user" && m.role !== "assistant") return null;
            const imageEntry = m.content.find((c) => c.type === "image" && c.imageData);
            const text = m.content
              .filter((c) => c.type === "text" || c.type === "option")
              .map((c) => c.text.value)
              .join("\n");
            if (imageEntry) {
              const parts: any[] = [{ type: "image", image: imageEntry.imageData }];
              if (text) parts.push({ type: "text", text });
              return { role: m.role as "user" | "assistant", content: parts };
            }
            return { role: m.role as "user" | "assistant", content: text };
          })
          .filter(Boolean);

        // Append app context to user message
        let contextSuffix = "";
        if (getAppContext) {
          try {
            const ctx = getAppContext();
            if (ctx) {
              contextSuffix = "\n\n<app-context>\n" + ctx + "\n</app-context>";
            }
          } catch (e) {
            console.warn("Could not gather app context for AI:", e);
          }
        }

        const enrichedUserContent = userContent + contextSuffix;

        if (image) {
          history.push({
            role: "user",
            content: [
              { type: "image", image },
              { type: "text", text: enrichedUserContent },
            ],
          });
        } else {
          history.push({ role: "user", content: enrichedUserContent });
        }

        // Determine effective model (vision model if images present)
        const hasImages = history.some(
          (m: any) => Array.isArray(m.content) && m.content.some((p: any) => p.type === "image")
        );
        const effectiveModelName = hasImages && visionModel ? visionModel : modelName;
        setActiveModelName(effectiveModelName);
        const aiTag = `[AI ${provider}/${effectiveModelName}]`;
        console.log(`${aiTag} 👤 User:`, userContent + (image ? " [+image]" : ""));

        // Custom fetch: route through proxy, inject headers
        const customFetch = createProxyFetch();

        const aiModel = createAiModel({
          provider,
          modelName: effectiveModelName,
          baseURL: proxyUrl,
          fetch: customFetch,
        });

        const systemPrompt = systemPromptParts.join("\n");

        const requestMessages: any[] = [
          {
            role: "system",
            content: systemPrompt,
            providerOptions: {
              anthropic: { cacheControl: { type: "ephemeral" } },
            },
          },
          ...history,
        ];

        const shownStepTexts = new Set<string>();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        const response = await generateText({
          model: aiModel,
          messages: requestMessages,
          stopWhen: stepCountIs(maxSteps),
          abortSignal: abortController.signal,
          tools: {
            evaluate: tool({
              description: evaluateToolDescription,
              inputSchema: jsonSchema<{ jsCode: string }>({
                type: "object",
                properties: {
                  jsCode: {
                    type: "string",
                    description: `The JavaScript code to execute.
                      MUST BE a string,
                      MUST BE valid javascript code,
                      MUST USE return keyword if the code is expected to return a value,
                      MUST USE only the provided API functions and standard javascript`,
                  },
                },
                required: ["jsCode"],
              }),
              // @ts-ignore
              toModelOutput: ({ output }) => {
                const screenshots = consumePendingScreenshots();
                if (screenshots.length > 0) {
                  return {
                    type: "content" as const,
                    value: [
                      { type: "text" as const, text: String(output) },
                      ...screenshots.map((s) => ({
                        type: "image-data" as const,
                        data: s.base64,
                        mediaType: s.mimeType,
                      })),
                    ],
                  };
                }
                return { type: "text" as const, value: String(output) };
              },
              // @ts-ignore
              execute: async ({ jsCode }: { jsCode: string }): Promise<string> => {
                if (abortController.signal.aborted) {
                  throw new DOMException("Aborted", "AbortError");
                }
                const codeShort = jsCode.length > 200 ? jsCode.substring(0, 200) + "…" : jsCode;
                console.log(`${aiTag} 🔧 Tool evaluate:`, jsCode);
                try {
                  const output = await evaluate({ jsCode });
                  const resultStr =
                    typeof output === "string" ? output : JSON.stringify(output) || "success";
                  const resultShort =
                    resultStr.length > 200 ? resultStr.substring(0, 200) + "…" : resultStr;
                  console.log(`${aiTag} 🔧 Tool result:`, resultShort);
                  return resultStr;
                } catch (e: any) {
                  console.error(`${aiTag} 🔧 Tool error:`, e.message || String(e));
                  const detail = [
                    `Error: ${e.message || String(e)}`,
                    e.stack ? `Stack: ${e.stack.split("\n").slice(0, 3).join(" | ")}` : "",
                    `Failed code: ${codeShort}`,
                  ]
                    .filter(Boolean)
                    .join("\n");
                  // Note: No separate error message here – the error detail is
                  // returned to the AI as a tool result and will appear in the
                  // log output. Using role-error would be misleading (reserved
                  // for connection errors) and would duplicate the message.
                  return detail;
                }
              },
            }),
          },
          onStepFinish: (step) => {
            const reasoningText = step.reasoningText ?? "";
            if (reasoningText.trim()) {
              const short =
                reasoningText.length > 300 ? reasoningText.substring(0, 300) + "…" : reasoningText;
              console.log(`${aiTag} 💭 Thinking:`, short);
              // Thinking logs are console-only – they add noise to the chat UI
            }

            if (step.text && step.text.trim()) {
              console.log(`${aiTag} 🤖 Step:`, step.text);
              shownStepTexts.add(step.text);
              setMessages((prev) => [...prev, createMessage("assistant", [step.text])]);
            }

            if (step.toolCalls && step.toolCalls.length > 0) {
              for (let i = 0; i < step.toolCalls.length; i++) {
                const tc = step.toolCalls[i] as any;
                const code = tc.input?.jsCode ?? "";
                if (code) {
                  setMessages((prev) => [...prev, createMessage("code", [code])]);
                }
                const result =
                  step.toolResults?.find((tr: any) => tr.toolCallId === tc.toolCallId) ??
                  step.toolResults?.[i];
                const resultStr = result ? String((result as any).output ?? "") : "";
                const resultShort =
                  resultStr.length > 200 ? resultStr.substring(0, 200) + "…" : resultStr;
                console.log(`${aiTag} 🔧 Tool result:`, resultShort || "✓");
                // Show result in chat: always when verbose, otherwise only for non-trivial results
                const isTrivialSuccess =
                  !resultStr || resultStr.includes('"success"') || resultStr === "success";
                if (verboseToolResults || !isTrivialSuccess) {
                  setMessages((prev) => [
                    ...prev,
                    createMessage("log", [`→ ${resultShort || "✓"}`]),
                  ]);
                }
              }
            }
          },
        });

        console.log(
          `${aiTag} 🤖 Response (${response.steps?.length ?? 1} steps, ${response.usage?.totalTokens ?? "?"} tokens):`,
          response.text || "(no text)"
        );

        let finalText = response.text;
        if ((!finalText || finalText.trim() === "") && response.steps) {
          finalText = response.steps
            .map((step) => step.text)
            .filter((text) => text && text.trim() !== "")
            .join("\n");
        }

        if (!finalText || finalText.trim() === "") {
          const hasToolCalls =
            response.steps &&
            response.steps.some((step) => step.toolCalls && step.toolCalls.length > 0);
          finalText = hasToolCalls ? msgToolCallsDone : msgEmptyResponse;
        }

        if (!shownStepTexts.has(finalText)) {
          setMessages((prev) => [...prev, createMessage("assistant", [finalText])]);
        }
      } catch (err: any) {
        if (err.name === "AbortError" || abortControllerRef.current?.signal.aborted) {
          console.log("AI Chat cancelled by user");
          setMessages((prev) => [...prev, createMessage("assistant", [msgCancelled])]);
        } else {
          console.error("AI Chat Error:", err);
          setErrorPersisted(err.message || String(err));
          setMessages((prev) => [
            ...prev,
            createMessage("error", [msgConnectionErrorPrefix + (err.message || String(err))]),
          ]);
        }
      } finally {
        abortControllerRef.current = null;
        safeSetIsLoading(false);
      }
    },
    [
      createProxyFetch,
      evaluate,
      evaluateToolDescription,
      getAppContext,
      maxSteps,
      messages,
      modelName,
      msgCancelled,
      msgConnectionErrorPrefix,
      msgEmptyResponse,
      msgToolCallsDone,
      provider,
      safeSetIsLoading,
      setErrorPersisted,
      setMessages,
      systemPromptParts,
      verboseToolResults,
      visionModel,
    ]
  );

  const generateFenceRenderImage = useCallback(
    async (input: AiFenceRenderRequest): Promise<AiFenceRenderResult> => {
      setAiRenderIsLoading(true);
      setAiRenderError(null);

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const aspectRatio = input.aspectRatio ?? "16:9";
      const imageSize = input.imageSize ?? "2K";
      const modelForImageGeneration = input.modelName ?? imageGenerationModel;
      const aiTag = `[AI ${imageGenerationProvider}/${modelForImageGeneration}]`;

      const normalizeOpenRouterImageModel = (modelName: string): string => {
        if (modelName.includes("/")) return modelName;
        return `google/${modelName}`;
      };

      const hasEditBaseImage = Boolean(input.editBaseImageDataUrl);

      try {
        if (!input.snapshotDataUrl || !input.preserveMaskDataUrl || !input.depthMapDataUrl) {
          throw new Error(
            "snapshotDataUrl, preserveMaskDataUrl and depthMapDataUrl are required for fence render generation."
          );
        }

        const proxyFetch = createProxyFetch();
        let response: Response;
        let targetPath: string;

        if (imageGenerationProvider === "openrouter") {
          targetPath = "/chat/completions";
          const requestUrl = new URL(
            `${proxyUrl.replace(/\/$/, "")}${targetPath}`,
            window.location.origin
          ).toString();

          const renderPrompt = buildFenceRenderPrompt(input.prompt, hasEditBaseImage);
          const requestBody = {
            model: normalizeOpenRouterImageModel(modelForImageGeneration),
            messages: [
              {
                role: "user",
                content: buildOpenRouterFenceRenderContent({
                  prompt: renderPrompt,
                  snapshotDataUrl: input.snapshotDataUrl,
                  preserveMaskDataUrl: input.preserveMaskDataUrl,
                  depthMapDataUrl: input.depthMapDataUrl,
                  editBaseImageDataUrl: input.editBaseImageDataUrl,
                }),
              },
            ],
            modalities: ["image", "text"],
            image_config: {
              aspect_ratio: aspectRatio,
            },
          };

          console.log(`${aiTag} 🖼️ Fence render generation requested`, {
            targetPath,
            aspectRatio,
            imageSize,
          });

          response = await proxyFetch(requestUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: abortController.signal,
          });
        } else if (imageGenerationProvider === "google") {
          /*
           * Do not route this request through @ai-sdk/google's default base URL.
           * The GA Gemini 3.1 image endpoint is:
           *   /v1/models/gemini-3.1-flash-image:generateContent
           */
          targetPath =
            `/${imageGenerationApiVersion}/models/` +
            `${encodeURIComponent(modelForImageGeneration)}:generateContent`;

          const requestUrl = new URL(
            `${proxyUrl.replace(/\/$/, "")}${targetPath}`,
            window.location.origin
          ).toString();

          const renderPrompt = buildFenceRenderPrompt(input.prompt, hasEditBaseImage);
          const requestBody = {
            contents: [
              {
                role: "user",
                parts: buildGeminiFenceRenderParts({
                  prompt: renderPrompt,
                  snapshotDataUrl: input.snapshotDataUrl,
                  preserveMaskDataUrl: input.preserveMaskDataUrl,
                  depthMapDataUrl: input.depthMapDataUrl,
                  editBaseImageDataUrl: input.editBaseImageDataUrl,
                  hasEditBaseImage,
                }),
              },
            ],
            generationConfig: {
              responseModalities: ["IMAGE"],
              responseFormat: {
                image: {
                  aspectRatio,
                  imageSize,
                },
              },
            },
          };

          console.log(`${aiTag} 🖼️ Fence render generation requested`, {
            targetPath,
            aspectRatio,
            imageSize,
          });

          response = await proxyFetch(requestUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              // Keeps the request compatible with proxies that detect Google
              // requests by the native authentication header. The proxy should
              // replace this placeholder with the server-side API key.
              "x-goog-api-key": "dummy-key",
            },
            body: JSON.stringify(requestBody),
            signal: abortController.signal,
          });
        } else {
          throw new Error(
            `Fence render generation does not support provider "${imageGenerationProvider}".`
          );
        }

        const responseContentType = response.headers.get("content-type") || "";
        const responseBuffer = await response.arrayBuffer();
        const responseBytes = new Uint8Array(responseBuffer);
        const responseText = new TextDecoder("utf-8").decode(responseBytes);
        let responseJson: any;

        try {
          responseJson = responseText
            ? (JSON.parse(responseText) as GeminiGenerateContentResponse)
            : {};
        } catch {
          const normalizedContentType = responseContentType.toLowerCase();
          const isImageResponseHeader = normalizedContentType.startsWith("image/");
          const isOctetStream = normalizedContentType.startsWith("application/octet-stream");
          const detectedMimeType = detectImageMimeTypeFromBytes(responseBytes);
          const isBinaryImagePayload =
            response.ok &&
            responseBytes.length > 0 &&
            (isImageResponseHeader || (isOctetStream && Boolean(detectedMimeType)));

          if (isBinaryImagePayload) {
            const mimeType = responseContentType.split(";")[0] || detectedMimeType || "image/png";
            const base64 = uint8ArrayToBase64(responseBytes);

            console.log(`${aiTag} 🖼️ Fence render generated (binary image response via proxy)`);

            return {
              imageDataUrl: `data:${mimeType};base64,${base64}`,
              mimeType,
              base64,
              warnings: [
                "Gemini endpoint returned a binary image payload instead of JSON. Parsed as image response.",
              ],
              providerMetadata: {
                model: modelForImageGeneration,
                apiVersion: imageGenerationApiVersion,
                contentType: responseContentType,
                responseMode: "binary-image",
              },
            };
          }

          throw new Error(
            `Image request returned non-JSON data ` +
              `(${response.status} ${response.statusText}) for ${targetPath}: ` +
              responseText.slice(0, 500)
          );
        }

        if (!response.ok || responseJson.error) {
          const upstreamMessage =
            responseJson.error?.message ||
            responseText ||
            response.statusText ||
            "Unknown upstream error";

          throw new Error(
            `Image request failed ` +
              `(${response.status} ${response.statusText}) for ${targetPath}: ` +
              upstreamMessage
          );
        }

        const responseCostUsd = getResponseCostUsd(responseJson);
        const generatedImage =
          imageGenerationProvider === "openrouter"
            ? getOpenRouterGeneratedImage(responseJson)
            : getGeminiGeneratedImage(responseJson as GeminiGenerateContentResponse);

        if (!generatedImage) {
          const details =
            imageGenerationProvider === "google"
              ? getGeminiResponseDetails(responseJson as GeminiGenerateContentResponse)
              : "";
          throw new Error("The image model returned no image." + (details ? ` ${details}` : ""));
        }

        console.log(`${aiTag} 🖼️ Fence render generated`);

        return {
          imageDataUrl: `data:${generatedImage.mimeType};base64,${generatedImage.base64}`,
          mimeType: generatedImage.mimeType,
          base64: generatedImage.base64,
          costUsd: responseCostUsd ?? undefined,
          providerMetadata: {
            usageMetadata: responseJson.usageMetadata,
            usage: responseJson.usage,
            model: modelForImageGeneration,
            apiVersion: imageGenerationApiVersion,
            editedFromExistingImage: hasEditBaseImage,
            costUsd: responseCostUsd ?? undefined,
          },
        };
      } catch (err: any) {
        const message = err?.message || String(err);
        if (err?.name === "AbortError" || abortController.signal.aborted) {
          setAiRenderError(msgCancelled);
          throw new DOMException(msgCancelled, "AbortError");
        }
        console.error(`${aiTag} 🖼️ Fence render error:`, err);
        setAiRenderError(message);
        throw err;
      } finally {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
        setAiRenderIsLoading(false);
      }
    },
    [
      createProxyFetch,
      imageGenerationApiVersion,
      imageGenerationModel,
      imageGenerationProvider,
      msgCancelled,
      proxyUrl,
    ]
  );

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  return {
    postMessage,
    messages,
    setMessages,
    isLoading,
    error,
    cancel,
    /** Generates the photorealistic fence render from snapshot + preserve mask + depth map. */
    generateFenceRenderImage,
    aiRenderIsLoading,
    aiRenderError,
    /** The model name currently in use (may differ from `model` option when vision model is active) */
    activeModelName,
  };
}

